import React, { useState, useEffect } from 'react';
import { MdLock, MdVisibility, MdVisibilityOff } from 'react-icons/md';
import { Link, useNavigate, useParams, useLocation } from 'react-router-dom';
import InputField from '../components/ui/InputField';
import Logo from '../assets/manak-logo.png';
import toast from 'react-hot-toast';
import { BiLoaderAlt } from 'react-icons/bi';
import { useAppDispatch, useAppSelector } from '../redux/hooks';
import { resetPassword } from '../redux/actions/authActions';
import { clearAuthError } from '../redux/slices/authSlice';

const ResetPassword: React.FC = () => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { token } = useParams<{ token: string }>();
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const role = queryParams.get('role') || 'employee'; // Default to employee if not specified
  
  const { loading, error, resetPasswordSuccess } = useAppSelector(state => state.auth);
  const [formData, setFormData] = useState({
    password: '',
    confirmPassword: ''
  });

  // Clear any existing errors when component mounts
  useEffect(() => {
    dispatch(clearAuthError());
  }, [dispatch]);

  // Handle successful password reset
  useEffect(() => {
    if (resetPasswordSuccess) {
      toast.success('Password has been reset successfully!');
      setTimeout(() => {
        navigate('/auth/login');
      }, 3000);
    }
  }, [resetPasswordSuccess, navigate]);

  // Show error message if there is one
  useEffect(() => {
    if (error) {
      toast.error(error);
    }
  }, [error]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Validate passwords
    if (formData.password.length < 8) {
      toast.error('Password must be at least 8 characters long');
      return;
    }

    if (formData.password !== formData.confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }

    if (!token) {
      toast.error('Invalid reset token');
      return;
    }

    dispatch(resetPassword({
      token,
      password: formData.password,
      role
    }));
  };

  return (
    <div className="min-h-screen w-full flex items-center justify-center bg-white relative overflow-hidden">
      {/* Background Pattern SVG - Same as Login and Forgot Password pages */}
      <svg className="absolute inset-0 w-full h-full" xmlns="http://www.w3.org/2000/svg">
        <defs>
          <pattern
            id="wave-pattern"
            patternUnits="userSpaceOnUse"
            width="100"
            height="8"
            patternTransform="rotate(0)"
          >
            <path
              d="M0,4 C12.5,0 12.5,8 25,4 C37.5,0 37.5,8 50,4 C62.5,0 62.5,8 75,4 C87.5,0 87.5,8 100,4"
              fill="none"
              stroke="rgba(232, 180, 180, 1)"
              strokeWidth="0.6"
            />
          </pattern>
        </defs>
        <rect width="100%" height="100%" fill="url(#wave-pattern)" />
      </svg>

      {/* Main Content - Updated to match Login page structure */}
      <div className="flex flex-col md:flex-row w-full max-w-6xl px-4 md:px-8 z-10">
        {/* Use full width on mobile, half width on desktop */}
        <div className="w-full md:w-1/2 md:mx-auto flex justify-center">
          <div className="bg-white p-6 md:p-10 rounded-2xl shadow-lg w-full max-w-xl">
            <div className="text-center">
              <img
                className="mx-auto h-12 w-auto"
                src={Logo}
                alt="Manak Logo"
              />
              <h2 className="mt-6 text-3xl font-extrabold text-red-600">
                Reset Password
              </h2>
              <p className="mt-2 text-sm text-gray-600">
                Enter your new password below
              </p>
            </div>

            <form className="mt-8 space-y-6" onSubmit={handleSubmit}>
              <div className="rounded-md shadow-sm -space-y-px">
                {/* New Password Field */}
                <div className="relative mb-4">
                  <InputField
                    label="New Password"
                    type="password"
                    value={formData.password}
                    onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                    placeholder="Enter new password"
                    icon={<MdLock className="w-5 h-5" />}
                    required
                    labelClassName="text-red-600"
                  />
                </div>

                {/* Confirm Password Field */}
                <div className="relative">
                  <InputField
                    label="Confirm Password"
                    type="password"
                    value={formData.confirmPassword}
                    onChange={(e) => setFormData({ ...formData, confirmPassword: e.target.value })}
                    placeholder="Confirm new password"
                    icon={<MdLock className="w-5 h-5" />}
                    required
                    labelClassName="text-red-600"
                  />
                </div>
              </div>

              <div>
                <button
                  type="submit"
                  disabled={loading}
                  className={`group relative w-full flex justify-center py-3 px-4 border border-transparent text-sm font-medium rounded-md text-white bg-red-600 hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 ${
                    loading ? 'opacity-70 cursor-not-allowed' : ''
                  }`}
                >
                  {loading ? (
                    <>
                      <BiLoaderAlt className="animate-spin -ml-1 mr-3 h-5 w-5" />
                      Resetting Password...
                    </>
                  ) : (
                    'Reset Password'
                  )}
                </button>
              </div>

              <div className="text-center">
                <Link
                  to="/auth/login"
                  className="font-medium text-red-600 hover:text-red-500"
                >
                  Back to Login
                </Link>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ResetPassword;
